import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState } from "@/components/widgets";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Loader2, RefreshCcw, Settings2 } from "lucide-react";
import { RatingCard } from "@/components/ratings/RatingCard";
import { RatingsOverviewPanel } from "@/components/ratings/OverviewPanel";
import { NegativeReviewsPanel } from "@/components/ratings/NegativeReviewsPanel";

export default function GoogleRatingsPage() {
  const { user, fnFilter } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const isAdmin = user?.permissions?.can_manage_settings;
  const effectiveFn = user?.function_scope !== "Both" ? user.function_scope : fnFilter;

  const load = () => {
    setLoading(true);
    api.get("/google-ratings/summary", { params: { function: effectiveFn } })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(errMsg(e)))
      .finally(() => setLoading(false));
  };

  useEffect(load, [effectiveFn]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchNow = async () => {
    setFetching(true);
    try {
      const r = await api.post("/google-ratings/fetch");
      toast.success(`Fetched ${r.data.fetched} listing${r.data.fetched !== 1 ? "s" : ""}`
        + (r.data.failed ? ` · ${r.data.failed} failed` : "")
        + (r.data.skipped_demo ? ` · ${r.data.skipped_demo} demo skipped` : ""));
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setFetching(false);
    }
  };

  if (loading || !data) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-24" />
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={`gr-skel-${i}`} className="h-64" />)}
        </div>
      </div>
    );
  }

  const anyLinked = data.locations.some((l) => l.cards.some((c) => c.linked));

  return (
    <div data-testid="google-ratings-page">
      <PageHeader
        title="Google Ratings"
        subtitle="Public Google listing ratings · refreshed daily · recognition only — never feeds escalation or complaint scores"
      >
        {isAdmin && (
          <>
            <Button variant="outline" onClick={() => navigate("/settings?tab=google-ratings")}
              data-testid="ratings-settings-button">
              <Settings2 size={15} className="mr-1.5" /> Configure
            </Button>
            <Button variant="outline" onClick={fetchNow} disabled={fetching}
              data-testid="ratings-fetch-now-button">
              {fetching ? <Loader2 size={15} className="animate-spin mr-1.5" /> : <RefreshCcw size={15} className="mr-1.5" />}
              Fetch now
            </Button>
          </>
        )}
      </PageHeader>

      <div className="space-y-4">
        {data.overview && <RatingsOverviewPanel overview={data.overview} />}

        {/* Leadership early-warning: 1-2 star reviews captured recently */}
        {data.overview && <NegativeReviewsPanel />}

        {!anyLinked && (
          <Card className="py-10">
            <EmptyState
              title="No Google listings linked yet"
              subtitle={isAdmin ? "Open Configure to map each location's Google listing — the built-in lookup finds the Place ID for you." : "Ratings appear once the admin links each location's Google listing."}
            />
          </Card>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="ratings-locations-grid">
          {data.locations.map((loc) => (
            <Card key={loc.outlet_id} className="p-3.5" data-testid={`ratings-location-${loc.code}`}>
              <div className="flex items-center gap-2 mb-2.5">
                <p className="font-serif text-base text-[hsl(var(--cx-ink))]">{loc.name}</p>
                <span className="text-xs text-muted-foreground">{loc.code}</span>
                {loc.outlet_id === data.user_outlet_id && (
                  <Badge variant="outline" className="text-[0.6rem] border-[hsl(var(--cx-gold))] text-[hsl(var(--cx-gold))]">You</Badge>
                )}
              </div>
              <div className="flex gap-2.5 flex-wrap">
                {loc.cards.map((c) => (
                  <RatingCard key={`${loc.outlet_id}-${c.function}`} card={c} outletCode={loc.code} />
                ))}
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
